import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Btn } from '@/presentation/component/ui';

interface CoursePlayerNavbarProps {
  subcategoryId: string;
  subcategoryName: string;
  courseTitle: string;
  onPreviousLesson: () => void;
  onNextLesson: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
}

export const CoursePlayerNavbar = ({
  subcategoryId,
  subcategoryName,
  courseTitle,
  onPreviousLesson,
  onNextLesson,
  hasPrevious,
  hasNext,
}: CoursePlayerNavbarProps) => {
  const { t } = useTranslation();

  return (
    <nav className="bg-[var(--color-surface)] text-[var(--color-ink)] shadow-lg border-b border-[var(--color-line)]">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link
              to={`/subcategory/${subcategoryId}`}
              className="flex items-center gap-2 text-[var(--color-ink-soft)] hover:text-[var(--color-ink)] transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium hidden sm:inline">
                {t('coursePlayer.backToCourse')}
              </span>
            </Link>
          </div>

          <div className="flex-1 text-center mx-4 sm:mx-8 min-w-0">
            <h1 className="text-base sm:text-lg font-semibold truncate">{courseTitle}</h1>
            <p className="text-sm text-[var(--color-ink-faint)] truncate hidden sm:block">
              {subcategoryName}
            </p>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <Btn
              kind="default"
              size="sm"
              onClick={onPreviousLesson}
              disabled={!hasPrevious}
              icon={<ChevronLeft className="w-4 h-4" />}
            >
              <span className="hidden sm:inline">{t('coursePlayer.previousLesson')}</span>
            </Btn>

            <Btn
              kind="primary"
              size="sm"
              onClick={onNextLesson}
              disabled={!hasNext}
              iconRight={<ChevronRight className="w-4 h-4" />}
            >
              <span className="hidden sm:inline">
                {hasNext ? t('coursePlayer.completeAndContinue') : t('coursePlayer.complete')}
              </span>
            </Btn>
          </div>
        </div>
      </div>
    </nav>
  );
};
